import { supabase, DEFAULT_ORG_ID, TRAINING_BUCKET, applyOrgFilter, getActiveOrgOverride } from '../supabaseClient'
import { getCurrentStaff } from './auth'
import { getMexicoDateString } from '../dateUtils'
import { v4 as uuidv4 } from 'uuid'

// ============================================================================
// SERIES DE ENTRENAMIENTO (Entrenamientos Recurrentes)
// ============================================================================

export async function getSeriesEntrenamiento(grupoId = null, activo = null) {
  try {
    let query = supabase
      .from('series_entrenamiento')
      .select('*, grupos(nombre_del_grupo, rama, categoria_base), temporadas(nombre, fecha_inicio, fecha_fin)')
      .order('created_at', { ascending: false });

    if (grupoId) {
      query = query.eq('grupo_id', grupoId);
    }

    if (activo !== null) {
      query = query.eq('activo', activo);
    }

    query = applyOrgFilter(query);

    const { data, error } = await query;

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error obteniendo series de entrenamiento:', error);
    return { data: null, error };
  }
}

export async function getSerieById(serieId) {
  try {
    const { data, error } = await supabase
      .from('series_entrenamiento')
      .select('*, grupos(nombre_del_grupo, rama, categoria_base), temporadas(nombre, fecha_inicio, fecha_fin)')
      .eq('id', serieId)
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error obteniendo serie por ID:', error);
    return { data: null, error };
  }
}

export async function createSerieEntrenamiento(serieData) {
  try {
    const { data: createdSerie, error: insertError } = await supabase
      .from('series_entrenamiento')
      .insert([{
        ...serieData,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()
      .single();

    if (insertError) throw insertError;

    const { data, error } = await supabase
      .from('series_entrenamiento')
      .select('*, grupos(nombre_del_grupo)')
      .eq('id', createdSerie.id)
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error creando serie de entrenamiento:', error);
    return { data: null, error };
  }
}

export async function updateSerieEntrenamiento(serieId, updates) {
  try {
    const { hora_inicio, hora_fin, ...otherUpdates } = updates;

    if (Object.keys(otherUpdates).length > 0) {
      const { error: updateError } = await supabase
        .from('series_entrenamiento')
        .update({
          ...otherUpdates,
          updated_at: new Date().toISOString()
        })
        .eq('id', serieId);

      if (updateError) throw updateError;
    }

    if (hora_inicio !== undefined || hora_fin !== undefined) {
      const { error: rpcError } = await supabase.rpc('update_serie_hora', {
        p_serie_id: serieId,
        p_hora_inicio: hora_inicio || null,
        p_hora_fin: hora_fin || null
      });

      if (rpcError) {
        if (rpcError.code !== 'PGRST202' &&
            !rpcError.message.includes('not found') &&
            !rpcError.message.includes('does not exist')) {
          throw rpcError;
        }
        console.warn('Advertencia: función update_serie_hora no disponible. Ejecuta la migración 20251024_add_update_serie_hora_function.sql');
      }
    }

    const { data, error } = await supabase
      .from('series_entrenamiento')
      .select('*, grupos(nombre_del_grupo)')
      .eq('id', serieId)
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error actualizando serie de entrenamiento:', error);
    return { data: null, error };
  }
}

export async function deleteSerieEntrenamiento(serieId) {
  try {
    const { data, error } = await supabase
      .from('series_entrenamiento')
      .update({
        activo: false,
        updated_at: new Date().toISOString()
      })
      .eq('id', serieId)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error desactivando serie de entrenamiento:', error);
    return { data: null, error };
  }
}

export async function generarEventosDeSerie(serieId, fechaInicio, fechaFin) {
  try {
    const { data: serie, error: serieError } = await supabase
      .from('series_entrenamiento')
      .select('id, nombre, grupo_id, dias_semana, hora_inicio, hora_fin, fecha_inicio, fecha_fin')
      .eq('id', serieId)
      .single();

    if (serieError) throw serieError;

    const start = new Date(`${fechaInicio}T00:00:00`);
    const end = new Date(`${fechaFin}T00:00:00`);
    const dayNames = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
    let created = 0;
    let existing = 0;

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = getMexicoDateString(d);
      const dayKey = dayNames[d.getDay()];
      const inDay = Array.isArray(serie.dias_semana) && serie.dias_semana.includes(dayKey);
      if (!inDay) continue;

      const { data: already } = await supabase
        .from('sesiones')
        .select('id')
        .eq('grupo_id', serie.grupo_id)
        .eq('fecha', dateStr)
        .eq('tipo', 'entrenamiento')
        .eq('serie_entrenamiento_id', serie.id)
        .maybeSingle();

      if (already?.id) {
        existing += 1;
        continue;
      }

      const { error: insertError } = await supabase
        .from('sesiones')
        .insert({
          tipo: 'entrenamiento',
          grupo_id: serie.grupo_id,
          fecha: dateStr,
          hora_inicio: serie.hora_inicio || null,
          hora_fin: serie.hora_fin || null,
          titulo: serie.nombre || 'Entrenamiento',
          estatus: 'programado',
          serie_entrenamiento_id: serie.id,
          es_extraordinaria: false
        });

      if (insertError) throw insertError;
      created += 1;
    }

    return {
      data: {
        eventos_creados: created,
        eventos_existentes: existing,
        mensaje: `Sesiones creadas: ${created}. Existentes: ${existing}.`
      },
      error: null
    };
  } catch (error) {
    console.error('Error generando eventos desde serie:', error);
    return { data: null, error };
  }
}

export async function getEventosPorSerie(serieId, futurosOnly = false) {
  try {
    let query = supabase
      .from('sesiones')
      .select('*')
      .eq('serie_entrenamiento_id', serieId)
      .eq('tipo', 'entrenamiento')
      .order('fecha', { ascending: true });

    if (futurosOnly) {
      const hoy = getMexicoDateString();
      query = query.gte('fecha', hoy);
    }

    const { data, error } = await query;

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error obteniendo eventos por serie:', error);
    return { data: null, error };
  }
}

export async function getEventsByGroupAndDate(grupoId, fecha) {
  if (!grupoId || !fecha) return { data: [], error: null };
  try {
    const { data, error } = await supabase
      .from('sesiones')
      .select('*')
      .eq('grupo_id', grupoId)
      .eq('fecha', fecha)
      .order('hora_inicio', { ascending: true });

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    return { data: [], error };
  }
}

export async function getAsistenciaMensual(grupoId, mes, anio, serieId = null) {
  try {
    const { data: grupo, error: grupoError } = await supabase
      .from('grupos')
      .select('id, nombre_del_grupo, categoria_base')
      .eq('id', grupoId)
      .single();

    if (grupoError) throw grupoError;

    const { data: jugadoresRelaciones, error: jugadoresError } = await supabase
      .from('jugadores_grupos')
      .select('jugador_id, jugadores(id, nombre_completo, numero)')
      .eq('grupo_id', grupoId)
      .eq('deleted', false)
      .eq('estatus', 'Activo');

    if (jugadoresError) throw jugadoresError;

    const jugadores = jugadoresRelaciones.map(jg => ({
      ...jg.jugadores,
      id: jg.jugador_id,
      categoria: grupo.categoria_base || ''
    }));

    const fechaInicio = `${anio}-${mes.toString().padStart(2, '0')}-01`;
    const ultimoDia = new Date(anio, mes, 0).getDate();
    const fechaFin = `${anio}-${mes.toString().padStart(2, '0')}-${ultimoDia}`;

    // Cargar serie(s) para saber los días programados
    let seriesQuery = supabase
      .from('series_entrenamiento')
      .select('id, dias_semana, fecha_inicio, fecha_fin')
      .eq('grupo_id', grupoId)
      .eq('activo', true);
    if (serieId) seriesQuery = seriesQuery.eq('id', serieId);
    const { data: seriesData } = await seriesQuery;

    let eventosQuery = supabase
      .from('sesiones')
      .select('id, titulo, fecha, hora_inicio, tipo, serie_entrenamiento_id, es_extraordinaria')
      .eq('grupo_id', grupoId)
      .gte('fecha', fechaInicio)
      .lte('fecha', fechaFin)
      .eq('tipo', 'entrenamiento')
      .order('fecha', { ascending: true });

    if (serieId) {
      eventosQuery = eventosQuery.eq('serie_entrenamiento_id', serieId);
    }

    const { data: eventosRaw, error: eventosError } = await eventosQuery;
    if (eventosError) throw eventosError;

    // Calcular días programados en el mes (base para porcentaje)
    const calcularDiasProgramados = () => {
      if (!seriesData?.length) return 0;
      const dayNameToIndex = { domingo: 0, lunes: 1, martes: 2, miercoles: 3, jueves: 4, viernes: 5, sabado: 6 };
      const diasSet = new Set();
      for (const serie of seriesData) {
        const diasSemana = (serie.dias_semana || []).map((d) => dayNameToIndex[d]).filter((d) => d !== undefined);
        if (!diasSemana.length) continue;
        const start = new Date(`${fechaInicio}T00:00:00`);
        const end = new Date(`${fechaFin}T00:00:00`);
        // Respetar rango de la serie
        const serieStart = serie.fecha_inicio ? new Date(`${serie.fecha_inicio}T00:00:00`) : start;
        const serieEnd = serie.fecha_fin ? new Date(`${serie.fecha_fin}T00:00:00`) : end;
        const effectiveStart = start > serieStart ? start : serieStart;
        const effectiveEnd = end < serieEnd ? end : serieEnd;
        for (let d = new Date(effectiveStart); d <= effectiveEnd; d.setDate(d.getDate() + 1)) {
          if (diasSemana.includes(d.getDay())) {
            diasSet.add(d.toISOString().split('T')[0]);
          }
        }
      }
      // Agregar días con sesión real (extraordinarias o creadas fuera del
      // horario, p.ej. por asignación de programa que no marca la bandera).
      // El Set deduplica los que ya caen en días programados.
      (eventosRaw || []).forEach((e) => {
        diasSet.add(e.fecha);
      });
      return diasSet.size;
    };

    const diasProgramadosEnMes = calcularDiasProgramados();

    if (!eventosRaw || eventosRaw.length === 0) {
      return {
        data: {
          jugadores: [],
          eventos: [],
          mes,
          anio
        },
        error: null
      };
    }

    const eventosPorFecha = eventosRaw.reduce((map, evento) => {
      if (!map.has(evento.fecha)) {
        map.set(evento.fecha, []);
      }
      map.get(evento.fecha).push(evento);
      return map;
    }, new Map());

    const eventos = Array.from(eventosPorFecha.entries())
      .sort((a, b) => new Date(a[0]) - new Date(b[0]))
      .map(([fecha, eventosDelDia]) => {
        const ordenados = [...eventosDelDia].sort((a, b) => {
          if (!a.hora_inicio && !b.hora_inicio) return 0;
          if (!a.hora_inicio) return 1;
          if (!b.hora_inicio) return -1;
          return a.hora_inicio.localeCompare(b.hora_inicio);
        });
        return {
          id: ordenados[0].id,
          fecha,
          nombre: ordenados[0].titulo || 'Entrenamiento',
          hora: ordenados[0].hora_inicio,
          tipo_evento: ordenados[0].tipo,
          eventoIds: ordenados.map(e => e.id)
        };
      });

    const eventosIds = eventos.flatMap(e => e.eventoIds);
    const { data: asistencias, error: asistenciasError } = await supabase
      .from('asistencias')
      .select('sesion_id, jugador_id, presente')
      .in('sesion_id', eventosIds);

    if (asistenciasError) throw asistenciasError;

    const asistenciaMap = asistencias.reduce((map, asistencia) => {
      map.set(`${asistencia.jugador_id}-${asistencia.sesion_id}`, asistencia);
      return map;
    }, new Map());

      const estadoDesdeAsistencia = (presente) => {
        if (presente === 'exento') return 'exento';
        if (presente === true || presente === 'presente') return 'asistio';
        if (presente === 'retardo') return 'retardo';
        if (presente === false || presente === 'ausente') return 'ausente';
        if (presente === 'justificado') return 'justificado';
        return 'sin_registrar';
      };

      const resolverEstado = (jugadorId, eventoAgrupado) => {
        let estado = 'sin_registrar';
        for (const eventoId of eventoAgrupado.eventoIds) {
          const asistencia = asistenciaMap.get(`${jugadorId}-${eventoId}`);
          const estadoActual = estadoDesdeAsistencia(asistencia?.presente);
          if (estadoActual === 'asistio') {
            return 'asistio';
          }
          if (estadoActual === 'retardo' && estado !== 'asistio') {
            estado = 'retardo';
          } else if (estadoActual === 'justificado' && estado !== 'asistio' && estado !== 'retardo') {
            estado = 'justificado';
          } else if (estadoActual === 'exento' && estado === 'sin_registrar') {
            estado = 'exento';
          } else if (estadoActual === 'ausente' && estado === 'sin_registrar') {
            estado = 'ausente';
          }
        }
        return estado;
      };

    const matriz = jugadores.map(jugador => {
      const asistenciasPorEvento = {};

      eventos.forEach(evento => {
        const estado = resolverEstado(jugador.id, evento);
        asistenciasPorEvento[evento.fecha] = {
          estado,
          eventoNombre: evento.nombre,
          eventoIds: evento.eventoIds
        };
      });

      const eventosValidos = Object.values(asistenciasPorEvento).filter(
        a => a.estado !== 'exento'
      );
      const totalRegistrados = eventosValidos.length;
      const exentosCount = Object.values(asistenciasPorEvento).length - eventosValidos.length;
      // Base para porcentaje: días programados en el mes (no solo los
      // registrados), menos los días en que este jugador estuvo exento.
      const totalBase = Math.max(diasProgramadosEnMes - exentosCount, totalRegistrados);
      const asistenciasCount = eventosValidos.filter(
        a => a.estado === 'asistio' || a.estado === 'retardo'
      ).length;
      const retardoCount = eventosValidos.filter(
        a => a.estado === 'retardo'
      ).length;
      const faltasCount = eventosValidos.filter(
        a => a.estado === 'ausente'
      ).length;
      const porcentaje = totalBase > 0
        ? Math.round((asistenciasCount / totalBase) * 100)
        : 0;

        return {
          jugador,
          asistencias: asistenciasPorEvento,
          estadisticas: {
            total: totalBase,
            registrados: totalRegistrados,
            asistio: asistenciasCount,
            retardo: retardoCount,
            falto: faltasCount,
            porcentaje
          }
        };
    });

    return {
      data: {
        jugadores: matriz,
        eventos,
        mes,
        anio
      },
      error: null
    };
  } catch (error) {
    console.error('Error obteniendo asistencia mensual:', error);
    return { data: null, error };
  }
}

export async function getEntrenamientosHoy() {
  try {
    const hoy = getMexicoDateString();

    // Un solo query con JOINs — sin N+1
    const { data, error } = await supabase
      .from('sesiones')
      .select(`
        *,
        grupos(nombre_del_grupo, rama,
          jugadores_grupos(id, jugador_id, estatus, activo, deleted)
        ),
        series_entrenamiento(nombre, descripcion),
        asistencias(id)
      `)
      .eq('fecha', hoy)
      .eq('tipo', 'entrenamiento')
      .order('hora_inicio', { ascending: true });

    if (error) throw error;

    const eventosConStats = (data || []).map((evento) => {
      const jugadoresActivos = (evento.grupos?.jugadores_grupos || [])
        .filter((jg) => jg.activo !== false && !jg.deleted && jg.estatus === 'Activo');
      const seen = new Set();
      let totalJugadores = 0;
      jugadoresActivos.forEach((jg) => {
        if (!seen.has(jg.jugador_id)) { seen.add(jg.jugador_id); totalJugadores++; }
      });
      const asistenciasRegistradas = (evento.asistencias || []).length;
      return {
        ...evento,
        asistencias: undefined, // limpiar del resultado
        stats: {
          totalJugadores,
          asistenciasRegistradas,
          completo: asistenciasRegistradas >= totalJugadores
        }
      };
    });

    return { data: eventosConStats, error: null };
  } catch (error) {
    console.error('Error obteniendo entrenamientos de hoy:', error);
    return { data: null, error };
  }
}

// ============================================================================
// PROGRAMAS DE ENTRENAMIENTO
// ============================================================================

const buildArrayValue = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean)
  }
  if (typeof value === 'string' && value.trim().length) {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
  }
  return []
}

export const getTrainingFilePublicUrl = (storagePath) => {
  if (!storagePath) return null
  const cleanPath = storagePath.replace(/^\/+/, '')
  const { data } = supabase.storage.from(TRAINING_BUCKET).getPublicUrl(cleanPath)
  return data?.publicUrl || null
}

export const uploadTrainingProgramAsset = async (file, programId = 'tmp') => {
  if (!file) {
    return { data: null, error: new Error('Archivo inválido') }
  }

  const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_')
  const folder = programId || 'tmp'
  const path = `${folder}/${Date.now()}_${sanitizedName}`

  const { error: uploadError } = await supabase.storage
    .from(TRAINING_BUCKET)
    .upload(path, file, {
      upsert: false,
      cacheControl: '3600',
      contentType: file.type || 'application/octet-stream'
    })

  if (uploadError) {
    return { data: null, error: uploadError }
  }

  const publicUrl = getTrainingFilePublicUrl(path)

  return {
    data: {
      file_name: file.name,
      file_type: file.type,
      file_size: file.size,
      storage_path: path,
      file_url: publicUrl
    },
    error: uploadError || null
  }
}

export const generateTrainingFileUrl = async (storagePath, expiresIn = 3600) => {
  if (!storagePath) {
    return { data: null, error: new Error('Ruta no válida') }
  }

  const cleanPath = (path) => path.replace(/^\/+/, '')
  const attempt = async (path) => {
    return supabase.storage
      .from(TRAINING_BUCKET)
      .createSignedUrl(cleanPath(path), expiresIn)
  }

  const firstAttempt = await attempt(storagePath)
  if (!firstAttempt.error) {
    return firstAttempt
  }

  const slashIndex = storagePath.indexOf('/')
  if (slashIndex !== -1) {
    const trimmed = storagePath.slice(slashIndex + 1)
    return attempt(trimmed)
  }

  return firstAttempt
}

export const createTrainingProgram = async (programData) => {
  const orgId = getActiveOrgOverride() || programData.org_id || (await getCurrentStaff())?.data?.org_id || DEFAULT_ORG_ID
  const payload = {
    titulo: programData.titulo,
    descripcion: programData.descripcion || null,
    tags: buildArrayValue(programData.tags),
    deportes: buildArrayValue(programData.deportes),
    nivel: programData.nivel || null,
    created_by: programData.created_by || null,
    org_id: orgId
  }

  const { data, error } = await supabase
    .from('programas_entrenamiento')
    .insert(payload)
    .select(`
      *,
      archivos:programas_entrenamiento_archivos(*)
    `)
    .single()

  return { data, error }
}

export const updateTrainingProgram = async (programId, updates) => {
  const payload = {
    ...updates,
    tags: updates.tags !== undefined ? buildArrayValue(updates.tags) : undefined,
    deportes: updates.deportes !== undefined ? buildArrayValue(updates.deportes) : undefined,
    nivel: updates.nivel ?? null
  }

  const { data, error } = await supabase
    .from('programas_entrenamiento')
    .update(payload)
    .eq('id', programId)
    .select(`
      *,
      archivos:programas_entrenamiento_archivos(*)
    `)
    .single()

  return { data, error }
}

export const archiveTrainingProgram = async (programId) => {
  const { data, error } = await supabase
    .from('programas_entrenamiento')
    .update({
      activo: false,
      deleted_at: new Date().toISOString()
    })
    .eq('id', programId)
    .select()
    .single()

  return { data, error }
}

export const attachProgramFiles = async (programId, files = []) => {
  if (!programId || !files.length) {
    return { data: [], error: null }
  }

  const payload = files.map((file) => ({
    programa_id: programId,
    file_name: file.file_name,
    file_type: file.file_type,
    file_size: file.file_size,
    file_url: file.file_url || null,
    storage_path: file.storage_path
  }))

  const { data, error } = await supabase
    .from('programas_entrenamiento_archivos')
    .insert(payload)
    .select()

  return { data, error }
}

export const deleteProgramFile = async (fileId) => {
  const { data, error } = await supabase
    .from('programas_entrenamiento_archivos')
    .delete()
    .eq('id', fileId)
    .select()
    .single()

  return { data, error }
}

export const assignProgramToAgenda = async (programId, assignments = []) => {
  if (!programId || !assignments.length) {
    return { data: [], error: null }
  }

  const orgId = getActiveOrgOverride() || (await getCurrentStaff())?.data?.org_id || DEFAULT_ORG_ID

  const payload = assignments
    .filter((assignment) => assignment.fecha)
    .map((assignment) => ({
      programa_id: programId,
      fecha: assignment.fecha,
      grupo_id: assignment.grupo_id || null,
      org_id: orgId,
      notas: assignment.notas || null,
      etiquetas: buildArrayValue(assignment.etiquetas),
      estado: assignment.estado || 'publicado',
      activo: assignment.activo !== undefined ? assignment.activo : true,
      created_by: assignment.created_by || null
    }))

  if (!payload.length) {
    return { data: [], error: null }
  }

  const { data, error } = await supabase
    .from('programas_entrenamiento_agenda')
    .upsert(payload, {
      onConflict: 'programa_id,fecha,grupo_destino_uuid'
    })
    .select(`
      *,
      programa:programas_entrenamiento(*),
      grupo:grupos(id,nombre_del_grupo,categoria_base,rama)
    `)

  return { data, error }
}

export const removeProgramAssignment = async (assignmentId) => {
  const { data, error } = await supabase
    .from('programas_entrenamiento_agenda')
    .update({
      activo: false,
      estado: 'archivado'
    })
    .eq('id', assignmentId)
    .select()
    .single()

  return { data, error }
}

export const getTrainingProgramAgenda = async ({
  startDate,
  endDate,
  groupId = null,
  includeInactive = false
} = {}) => {
  let query = supabase
    .from('programas_entrenamiento_agenda')
    .select(`
      id,
      fecha,
      grupo_id,
      notas,
      etiquetas,
      estado,
      activo,
      created_at,
      programa:programas_entrenamiento (
        id,
        titulo,
        descripcion,
        tags,
        deportes,
        created_by,
        nivel,
        archivos:programas_entrenamiento_archivos (
          id,
          file_name,
          file_type,
          file_size,
          file_url,
          storage_path,
          created_at
        ),
        secciones:programa_secciones (
          id,
          duracion_seg,
          tipo,
          nombre,
          orden
        )
      ),
      grupo:grupos (
        id,
        nombre_del_grupo,
        categoria_base,
        rama
      )
    `)
    .order('fecha', { ascending: true })
    .order('created_at', { ascending: true })

  if (startDate) {
    query = query.gte('fecha', startDate)
  }

  if (endDate) {
    query = query.lte('fecha', endDate)
  }

  if (!includeInactive) {
    query = query.eq('activo', true)
  }

  if (groupId) {
    query = query.or(`grupo_id.eq.${groupId},grupo_id.is.null`)
  }

  query = applyOrgFilter(query)

  const { data, error } = await query
  return { data, error }
}

export const getTrainingProgramsLibrary = async ({
  search = '',
  tags = [],
  deportes = [],
  limit = 50,
  includeArchived = false
} = {}) => {
  let query = supabase
    .from('programas_entrenamiento')
    .select(`
      *,
      agenda:programas_entrenamiento_agenda (
        id,
        fecha,
        grupo:grupos(id, nombre_del_grupo)
      ),
      archivos:programas_entrenamiento_archivos (
        id,
        file_name,
        file_type,
        file_size,
        file_url,
        storage_path,
        created_at
      ),
      secciones:programa_secciones (
        id,
        duracion_seg,
        tipo,
        nombre,
        orden
      ),
      habilidades_relacion:programa_habilidades (
        habilidad_id,
        deporte_habilidades(id, nombre)
      )
    `)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (!includeArchived) {
    query = query.eq('activo', true)
  }

  if (search) {
    query = query.ilike('titulo', `%${search}%`)
  }

  if (Array.isArray(tags) && tags.length) {
    query = query.contains('tags', tags)
  }

  if (Array.isArray(deportes) && deportes.length) {
    query = query.contains('deportes', deportes)
  }

  query = applyOrgFilter(query)

  const { data, error } = await query
  return { data, error }
}

// ============================================================================
// HABILIDADES POR DEPORTE
// ============================================================================

export const getDeporteHabilidades = async (deporte, { incluirInactivas = false } = {}) => {
  let query = supabase
    .from('deporte_habilidades')
    .select('*')
    .eq('deporte', deporte)

  if (!incluirInactivas) {
    query = query.eq('activo', true)
  }

  // Misma fuga que en las plantillas: al impersonar se veian las habilidades de las otras
  // orgs mezcladas con las propias.
  query = applyOrgFilter(query)

  query = query.order('orden', { ascending: true })

  const { data, error } = await query
  return { data, error }
}

export const createHabilidad = async ({ deporte, nombre, org_id, orden = null }) => {
  let ordenFinal = orden

  if (ordenFinal === null || ordenFinal === undefined) {
    const { data: maxRow } = await supabase
      .from('deporte_habilidades')
      .select('orden')
      .eq('deporte', deporte)
      .order('orden', { ascending: false })
      .limit(1)
      .maybeSingle()
    ordenFinal = (maxRow?.orden || 0) + 1
  }

  const { data, error } = await supabase
    .from('deporte_habilidades')
    .insert([{
      deporte,
      nombre: nombre.trim(),
      org_id,
      orden: ordenFinal,
      activo: true
    }])
    .select()
    .single()

  return { data, error }
}

export const updateHabilidad = async (id, updates) => {
  const payload = {
    ...updates,
    updated_at: new Date().toISOString()
  }
  if (payload.nombre) {
    payload.nombre = payload.nombre.trim()
  }

  const { data, error } = await supabase
    .from('deporte_habilidades')
    .update(payload)
    .eq('id', id)
    .select()
    .single()

  return { data, error }
}

export const archiveHabilidad = async (id) => {
  const { data, error } = await supabase
    .from('deporte_habilidades')
    .update({
      activo: false,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single()

  return { data, error }
}

// ============================================================================
// PLANTILLAS DE SECCION (reutilizables entre programas)
// ============================================================================

export const getSeccionTemplates = async ({
  tipo = null,
  incluirInactivas = false
} = {}) => {
  let query = supabase
    .from('seccion_templates')
    .select('*')

  if (tipo) {
    query = query.eq('tipo', tipo)
  }
  if (!incluirInactivas) {
    query = query.eq('activo', true)
  }

  // Sin esto, un platform_admin "viendo como" una org veia TAMBIEN las plantillas de las
  // demas: su RLS es `has_org_access(org_id) OR is_platform_admin()`, y el segundo termino
  // le abre todo. Medido en produccion: 38 plantillas donde RAIDERS tiene 37, con una de
  // MYKLUBB Demo colandose -- y con el mismo nombre, asi que parecia un duplicado.
  // Para un usuario normal esto es inofensivo: sin override, applyOrgFilter no filtra nada.
  query = applyOrgFilter(query)

  query = query.order('nombre', { ascending: true })

  const { data, error } = await query
  return { data, error }
}

export const createSeccionTemplate = async ({
  org_id,
  nombre,
  duracion_seg,
  descripcion = null,
  tipo = 'otro',
  deporte = 'todos',
  habilidadIds = [],
  created_by = null
}) => {
  const { data, error } = await supabase
    .from('seccion_templates')
    .insert([{
      org_id,
      nombre: nombre.trim(),
      duracion_seg,
      descripcion: descripcion || null,
      tipo,
      deporte,
      habilidad_ids: habilidadIds,
      created_by,
      activo: true
    }])
    .select()
    .single()

  return { data, error }
}

export const updateSeccionTemplate = async (id, updates) => {
  const { habilidadIds, ...rest } = updates
  const payload = {
    ...rest,
    updated_at: new Date().toISOString()
  }
  if (payload.nombre !== undefined) {
    payload.nombre = payload.nombre.trim()
  }
  if (habilidadIds !== undefined) {
    payload.habilidad_ids = habilidadIds
  }

  const { data, error } = await supabase
    .from('seccion_templates')
    .update(payload)
    .eq('id', id)
    .select()
    .single()

  return { data, error }
}

export const archiveSeccionTemplate = async (id) => {
  const { data, error } = await supabase
    .from('seccion_templates')
    .update({
      activo: false,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single()

  return { data, error }
}

export const restoreSeccionTemplate = async (id) => {
  const { data, error } = await supabase
    .from('seccion_templates')
    .update({
      activo: true,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single()

  return { data, error }
}

export const restoreHabilidad = async (id) => {
  const { data, error } = await supabase
    .from('deporte_habilidades')
    .update({
      activo: true,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single()

  return { data, error }
}

export const reorderHabilidades = async (idsOrdenados) => {
  const results = []
  for (let i = 0; i < idsOrdenados.length; i++) {
    const { data, error } = await supabase
      .from('deporte_habilidades')
      .update({ orden: i + 1, updated_at: new Date().toISOString() })
      .eq('id', idsOrdenados[i])
      .select()
      .single()
    if (error) return { data: results, error }
    results.push(data)
  }
  return { data: results, error: null }
}

// ============================================================================
// SECCIONES DE PROGRAMA
// ============================================================================

export const getProgramaSecciones = async (programaId) => {
  const { data, error } = await supabase
    .from('programa_secciones')
    .select('*')
    .eq('programa_id', programaId)
    .order('orden', { ascending: true })

  return { data, error }
}

export const createProgramaSeccion = async ({
  programa_id,
  nombre,
  duracion_seg,
  descripcion = null,
  tipo = 'otro',
  orden = null
}) => {
  let ordenFinal = orden

  if (ordenFinal === null || ordenFinal === undefined) {
    const { data: maxRow } = await supabase
      .from('programa_secciones')
      .select('orden')
      .eq('programa_id', programa_id)
      .order('orden', { ascending: false })
      .limit(1)
      .maybeSingle()
    ordenFinal = (maxRow?.orden || 0) + 1
  }

  const { data, error } = await supabase
    .from('programa_secciones')
    .insert([{
      programa_id,
      nombre: nombre.trim(),
      duracion_seg,
      descripcion,
      tipo,
      orden: ordenFinal
    }])
    .select()
    .single()

  return { data, error }
}

export const updateProgramaSeccion = async (id, updates) => {
  const payload = {
    ...updates,
    updated_at: new Date().toISOString()
  }
  if (payload.nombre !== undefined) {
    payload.nombre = payload.nombre.trim()
  }

  const { data, error } = await supabase
    .from('programa_secciones')
    .update(payload)
    .eq('id', id)
    .select()
    .single()

  return { data, error }
}

export const deleteProgramaSeccion = async (id) => {
  const { data, error } = await supabase
    .from('programa_secciones')
    .delete()
    .eq('id', id)
    .select()
    .single()

  return { data, error }
}

export const reorderProgramaSecciones = async (programaId, idsOrdenados) => {
  const results = []
  for (let i = 0; i < idsOrdenados.length; i++) {
    const { data, error } = await supabase
      .from('programa_secciones')
      .update({ orden: i + 1, updated_at: new Date().toISOString() })
      .eq('id', idsOrdenados[i])
      .select()
      .single()
    if (error) return { data: results, error }
    results.push(data)
  }
  return { data: results, error: null }
}

// ============================================================================
// PROGRAMA <-> HABILIDADES (N:M)
// ============================================================================

export const getProgramaHabilidades = async (programaId) => {
  const { data, error } = await supabase
    .from('programa_habilidades')
    .select('habilidad_id, deporte_habilidades(id, nombre, deporte, orden)')
    .eq('programa_id', programaId)

  return { data, error }
}

export const setProgramaHabilidades = async (programaId, habilidadIds = []) => {
  const { error: deleteError } = await supabase
    .from('programa_habilidades')
    .delete()
    .eq('programa_id', programaId)

  if (deleteError) return { data: null, error: deleteError }

  if (!habilidadIds.length) {
    return { data: [], error: null }
  }

  const payload = habilidadIds.map((habilidad_id) => ({
    programa_id: programaId,
    habilidad_id
  }))

  const { data, error } = await supabase
    .from('programa_habilidades')
    .insert(payload)

  return { data, error }
}

// ============================================================================
// PROGRAMA COMPLETO (lectura y guardado orquestados)
// ============================================================================

export const getProgramaWithSeccionesYHabilidades = async (programaId) => {
  const { data: programa, error: errPrograma } = await supabase
    .from('programas_entrenamiento')
    .select('*')
    .eq('id', programaId)
    .single()
  if (errPrograma) return { data: null, error: errPrograma }

  const { data: secciones, error: errSecciones } = await supabase
    .from('programa_secciones')
    .select('*')
    .eq('programa_id', programaId)
    .order('orden', { ascending: true })
  if (errSecciones) return { data: null, error: errSecciones }

  const { data: habRows, error: errHab } = await supabase
    .from('programa_habilidades')
    .select('habilidad_id, deporte_habilidades(id, nombre, deporte, orden)')
    .eq('programa_id', programaId)
  if (errHab) return { data: null, error: errHab }

  const habilidades = (habRows || [])
    .map((r) => r.deporte_habilidades)
    .filter(Boolean)

  return {
    data: { ...programa, secciones: secciones || [], habilidades },
    error: null
  }
}

export const saveProgramaCompleto = async ({
  programaId = null,
  programa,
  secciones = [],
  habilidadIds = []
}) => {
  // 1) Crear o actualizar el programa
  let savedPrograma
  if (programaId) {
    const { data, error } = await supabase
      .from('programas_entrenamiento')
      .update({
        ...programa,
        updated_at: new Date().toISOString()
      })
      .eq('id', programaId)
      .select()
      .single()
    if (error) return { data: null, error }
    savedPrograma = data
  } else {
    const { data, error } = await supabase
      .from('programas_entrenamiento')
      .insert([programa])
      .select()
      .single()
    if (error) return { data: null, error }
    savedPrograma = data
  }

  const realProgramaId = savedPrograma.id

  // 2) Reemplazar secciones (estrategia: borrar todas y reinsertar)
  if (programaId) {
    const { error } = await supabase
      .from('programa_secciones')
      .delete()
      .eq('programa_id', realProgramaId)
    if (error) return { data: null, error }
  }

  if (secciones.length) {
    const payload = secciones.map((s, idx) => ({
      programa_id: realProgramaId,
      orden: idx + 1,
      nombre: s.nombre.trim(),
      duracion_seg: s.duracion_seg,
      descripcion: s.descripcion || null,
      tipo: s.tipo,
      habilidad_ids: Array.isArray(s.habilidad_ids) ? s.habilidad_ids : []
    }))
    const { error } = await supabase
      .from('programa_secciones')
      .insert(payload)
      .select()
    if (error) return { data: null, error }
  }

  // 3) Sincronizar habilidades
  const { error: errDelHab } = await supabase
    .from('programa_habilidades')
    .delete()
    .eq('programa_id', realProgramaId)
  if (errDelHab) return { data: null, error: errDelHab }

  if (habilidadIds.length) {
    const habPayload = habilidadIds.map((habilidad_id) => ({
      programa_id: realProgramaId,
      habilidad_id
    }))
    const { error } = await supabase
      .from('programa_habilidades')
      .insert(habPayload)
    if (error) return { data: null, error }
  }

  return { data: savedPrograma, error: null }
}

// ============================================================================
// ASIGNACION DE PROGRAMAS A SESIONES
// ============================================================================

const findOrCreateSesion = async (grupoId, fecha) => {
  const { data: existente } = await supabase
    .from('sesiones')
    .select('*')
    .eq('grupo_id', grupoId)
    .eq('fecha', fecha)
    .eq('tipo', 'entrenamiento')
    .maybeSingle()

  if (existente?.id) return { data: existente, error: null }

  const { data: creada, error } = await supabase
    .from('sesiones')
    .insert([{
      tipo: 'entrenamiento',
      grupo_id: grupoId,
      fecha,
      titulo: 'Entrenamiento',
      estatus: 'programado',
      es_extraordinaria: false
    }])
    .select()
    .single()

  return { data: creada, error }
}

export const assignProgramaToSesiones = async ({
  programaId,
  fechas = [],
  grupoIds = [],
  relojCompartido = false,
  staffId = null,
  notas = null
}) => {
  if (!programaId) return { data: null, error: { message: 'programaId requerido' } }
  if (!fechas.length) return { data: null, error: { message: 'Necesitas al menos una fecha' } }
  if (!grupoIds.length) return { data: null, error: { message: 'Necesitas al menos un grupo' } }

  // 1) Verificar asignaciones existentes que se reemplazarian
  const { data: existentes } = await supabase
    .from('programas_entrenamiento_agenda')
    .select('id, fecha, grupo_id, grupo:grupos(nombre_del_grupo)')
    .in('grupo_id', grupoIds)
    .in('fecha', fechas)
    .eq('activo', true)

  // Generar sync_tokens por fecha si reloj compartido
  const tokensPorFecha = {}
  if (relojCompartido && grupoIds.length >= 2) {
    fechas.forEach((fecha) => {
      tokensPorFecha[fecha] = uuidv4()
    })
  }

  // 2) Build payload de asignaciones
  const asignacionesPayload = []
  for (const fecha of fechas) {
    for (const grupoId of grupoIds) {
      asignacionesPayload.push({
        programa_id: programaId,
        fecha,
        grupo_id: grupoId,
        notas,
        estado: 'publicado',
        activo: true,
        created_by: staffId,
        reloj_compartido_entre_grupos: Boolean(tokensPorFecha[fecha]),
        grupo_sync_token: tokensPorFecha[fecha] || null
      })
    }
  }

  // 3) Desactivar asignaciones existentes
  if (existentes && existentes.length > 0) {
    const ids = existentes.map((e) => e.id)
    const { error: errArchive } = await supabase
      .from('programas_entrenamiento_agenda')
      .update({ activo: false, estado: 'archivado' })
      .in('id', ids)
    if (errArchive) return { data: null, error: errArchive }
  }

  // 4) Insertar nuevas asignaciones
  const { data: insertadas, error: errInsert } = await supabase
    .from('programas_entrenamiento_agenda')
    .insert(asignacionesPayload)
    .select('*, grupo:grupos(id, nombre_del_grupo)')

  if (errInsert) return { data: null, error: errInsert }

  // 5) Para cada asignacion, vincular sesion (find or create) y guardar programa_asignado_id
  const vinculadas = []
  for (const asignacion of insertadas) {
    const { data: sesion, error: errSes } = await findOrCreateSesion(
      asignacion.grupo_id,
      asignacion.fecha
    )
    if (errSes) {
      return { data: vinculadas, error: errSes }
    }

    const { error: errLink } = await supabase
      .from('sesiones')
      .update({ programa_asignado_id: asignacion.id })
      .eq('id', sesion.id)
    if (errLink) {
      return { data: vinculadas, error: errLink }
    }

    vinculadas.push({ ...asignacion, sesion_id: sesion.id })
  }

  return { data: vinculadas, error: null }
}

export const unassignPrograma = async (asignacionId) => {
  if (!asignacionId) return { data: null, error: { message: 'asignacionId requerido' } }

  const { data: asignacion, error: errGet } = await supabase
    .from('programas_entrenamiento_agenda')
    .select('id, programa_id, fecha, grupo_id')
    .eq('id', asignacionId)
    .single()
  if (errGet) return { data: null, error: errGet }

  const { data: sesion, error: errSes } = await supabase
    .from('sesiones')
    .select('id, programa_estado_en_vivo, programa_recorrido')
    .eq('programa_asignado_id', asignacionId)
    .maybeSingle()
  if (errSes) return { data: null, error: errSes }

  if (sesion?.programa_estado_en_vivo) {
    return {
      data: null,
      error: { message: 'La sesion ya tiene un programa en ejecucion. No se puede desasignar; solo terminar.' }
    }
  }

  if (sesion?.id) {
    const { error: errClear } = await supabase
      .from('sesiones')
      .update({ programa_asignado_id: null })
      .eq('id', sesion.id)
    if (errClear) return { data: null, error: errClear }
  }

  const { data, error } = await supabase
    .from('programas_entrenamiento_agenda')
    .update({ activo: false, estado: 'archivado' })
    .eq('id', asignacionId)
    .select()
    .single()

  return { data, error }
}

// ============================================================================
// MODO EN VIVO — control del programa cronometrado
// ============================================================================

export const getSesionEnVivo = async (sesionId) => {
  const { data, error } = await supabase
    .from('sesiones')
    .select(`
      id,
      grupo_id,
      programa_asignado_id,
      programa_estado_en_vivo,
      programa_recorrido,
      grupo:grupos(id, nombre_del_grupo),
      asignacion:programas_entrenamiento_agenda!sesiones_programa_asignado_id_fkey (
        id,
        fecha,
        grupo_sync_token,
        programa:programas_entrenamiento (
          id, titulo, descripcion, deportes,
          archivos:programas_entrenamiento_archivos(*),
          secciones:programa_secciones(
            id, orden, nombre, duracion_seg, descripcion, tipo
          )
        )
      )
    `)
    .eq('id', sesionId)
    .maybeSingle()

  if (error) return { data: null, error }

  if (data?.asignacion?.programa?.secciones) {
    data.asignacion.programa.secciones.sort((a, b) => a.orden - b.orden)
  }

  return { data, error: null }
}

const aplicarEstado = async (sesionId, nuevoEstado) => {
  return await supabase.rpc('fn_sync_grupo_estado', {
    p_sesion_id: sesionId,
    p_nuevo_estado: nuevoEstado
  })
}

export const iniciarPrograma = async ({ sesionId, programaId, syncToken = null }) => {
  const estado = {
    programa_id: programaId,
    iniciado_en: new Date().toISOString(),
    pausado_en: null,
    tiempo_pausado_acumulado_seg: 0,
    seccion_idx: 0,
    ajustes_seccion: {},
    secciones_saltadas: [],
    terminado_en: null,
    terminado_por_staff_id: null,
    sync_token: syncToken
  }
  const { error } = await aplicarEstado(sesionId, estado)
  return { data: estado, error }
}

export const pausarPrograma = async ({ sesionId, estadoActual }) => {
  if (estadoActual.pausado_en) return { data: estadoActual, error: null }
  const nuevo = {
    ...estadoActual,
    pausado_en: new Date().toISOString()
  }
  const { error } = await aplicarEstado(sesionId, nuevo)
  return { data: nuevo, error }
}

export const reanudarPrograma = async ({ sesionId, estadoActual }) => {
  if (!estadoActual.pausado_en) return { data: estadoActual, error: null }
  const pausadoEn = new Date(estadoActual.pausado_en).getTime()
  const ahora = Date.now()
  const acumuladoExtra = Math.floor((ahora - pausadoEn) / 1000)
  const nuevo = {
    ...estadoActual,
    pausado_en: null,
    tiempo_pausado_acumulado_seg:
      (estadoActual.tiempo_pausado_acumulado_seg || 0) + acumuladoExtra
  }
  const { error } = await aplicarEstado(sesionId, nuevo)
  return { data: nuevo, error }
}

export const extenderSeccion = async ({ sesionId, estadoActual, segundos }) => {
  const idx = String(estadoActual.seccion_idx)
  const ajustes = { ...(estadoActual.ajustes_seccion || {}) }
  ajustes[idx] = (ajustes[idx] || 0) + segundos
  const nuevo = { ...estadoActual, ajustes_seccion: ajustes }
  const { error } = await aplicarEstado(sesionId, nuevo)
  return { data: nuevo, error }
}

export const saltarSeccion = async ({ sesionId, estadoActual, totalSecciones, staffId = null }) => {
  const idxActual = estadoActual.seccion_idx
  const saltadas = [...(estadoActual.secciones_saltadas || []), String(idxActual)]

  if (idxActual + 1 >= totalSecciones) {
    const nuevo = {
      ...estadoActual,
      secciones_saltadas: saltadas,
      terminado_en: new Date().toISOString(),
      terminado_por_staff_id: staffId
    }
    const { error } = await aplicarEstado(sesionId, nuevo)
    return { data: nuevo, error, terminado: true }
  }

  const nuevo = {
    ...estadoActual,
    seccion_idx: idxActual + 1,
    secciones_saltadas: saltadas
  }
  const { error } = await aplicarEstado(sesionId, nuevo)
  return { data: nuevo, error, terminado: false }
}

export const seccionAnterior = async ({ sesionId, estadoActual }) => {
  if ((estadoActual.seccion_idx || 0) === 0) {
    return { data: estadoActual, error: null }
  }
  const nuevo = {
    ...estadoActual,
    seccion_idx: estadoActual.seccion_idx - 1
  }
  const { error } = await aplicarEstado(sesionId, nuevo)
  return { data: nuevo, error }
}

export const terminarPrograma = async ({ sesionId, staffId = null }) => {
  const { data: sesion } = await supabase
    .from('sesiones')
    .select('programa_estado_en_vivo')
    .eq('id', sesionId)
    .maybeSingle()
  const estadoActual = sesion?.programa_estado_en_vivo || {}
  const nuevoEstado = {
    ...estadoActual,
    terminado_en: new Date().toISOString(),
    terminado_por_staff_id: staffId
  }
  const { error: errMarcar } = await aplicarEstado(sesionId, nuevoEstado)
  if (errMarcar) {
    console.error('[terminarPrograma] error marcando terminado:', errMarcar)
    return { data: null, error: errMarcar }
  }

  const { data, error } = await supabase.rpc('generar_recorrido', { p_sesion_id: sesionId })
  if (error) {
    console.error('[terminarPrograma] error en generar_recorrido RPC:', error)
  }
  return { data, error }
}

export const subscribeToProgramaEstado = (sesionId, onChange) => {
  const channel = supabase
    .channel(`sesion-en-vivo-${sesionId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'sesiones',
        filter: `id=eq.${sesionId}`
      },
      (payload) => {
        const newRow = payload.new
        if (newRow) onChange(newRow)
      }
    )
    .subscribe()

  return () => {
    supabase.removeChannel(channel)
  }
}
